import { useRef, useEffect, useState, KeyboardEvent } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAIChat } from '@/hooks/useAIChat';
import { ChatMessage } from './ChatMessage';
import { AIWelcomeScreen } from './AIWelcomeScreen';
import { AISmartSuggestions } from './AISmartSuggestions';
import { AIDisclaimer } from './AIDisclaimer';
import { ThinkingBubble } from './ThinkingBubble';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ArrowUp } from 'lucide-react';
import { ChatStatus } from '@/types/ai-chat.types';
import { analytics } from '@/lib/analytics';

export const AIContentInterface = () => {
  const { t } = useTranslation();
  const { messages, status, isStreaming, streamingMessageId, sendMessage } = useAIChat();
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isBusy = status === ChatStatus.SENDING || isStreaming;
  const hasMessages = messages.length > 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, status]);

  const handleSend = () => {
    if (!input.trim() || isBusy) return;
    analytics.track('AI Message Sent', { source: 'input', message_length: input.trim().length });
    sendMessage(input);
    setInput('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (text: string) => {
    analytics.track('AI Message Sent', { source: 'suggestion', suggestion: text });
    sendMessage(text);
  };

  // Filter out empty assistant messages during SENDING — ThinkingBubble covers that state
  const visibleMessages = status === ChatStatus.SENDING
    ? messages.filter((m) => !(m.role === 'assistant' && m.content === ''))
    : messages;

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-4 py-4 px-4">
          {!hasMessages ? (
            <>
              <AIWelcomeScreen />
              <AISmartSuggestions onSelect={handleSuggestion} />
            </>
          ) : (
            visibleMessages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isStreaming={isStreaming && streamingMessageId === message.id}
              />
            ))
          )}

          {status === ChatStatus.SENDING && <ThinkingBubble />}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-t px-4 pt-3 pb-2 shrink-0">
        <div className="relative flex items-end gap-2 bg-background rounded-xl border px-3 py-2 focus-within:ring-1 focus-within:ring-ring">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aiChat.inputPlaceholder')}
            className="flex-1 resize-none border-0 shadow-none focus-visible:ring-0 min-h-[40px] max-h-[160px] py-1 px-0 text-sm"
            rows={1}
            disabled={isBusy}
          />
          <Button
            size="icon"
            className="shrink-0 size-8 rounded-lg"
            onClick={handleSend}
            disabled={!input.trim() || isBusy}
          >
            <ArrowUp className="size-4" />
          </Button>
        </div>
        <AIDisclaimer />
      </div>
    </div>
  );
};
